import classNames from 'classnames';
import cn from 'classnames';
import React, { KeyboardEventHandler, memo, useCallback, useMemo, useState } from 'react';

import { Button, Space, Typography } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';

import { IBlock, TYPES } from '../../../../constants/promoCarousel';
import { IBlock as IBlockVariantB } from '../../../../constants/variant-b-promoCarouse';
import { getRegNumberToken } from '../../../../services/osago';
import { CreditForm } from '../CreditForm';
import { OsagoForm } from '../OsagoForm';

import styles from './blocks.module.scss';

export type IBlockProps = {
  item: IBlock;
  handleClick: (url: string) => void;
  darkTheme: boolean;
};

// TODO variant b
export type IBlockPropsVariantB = {
  item: IBlockVariantB;
  handleClick: (url: string) => void;
  darkTheme: boolean;
  isMobile?: boolean;
};

export interface IPromoBlockProps {
  setValue: (value) => void;
  handleEnterEvent: KeyboardEventHandler<HTMLInputElement>;
}

export const BigBlock = memo<IBlockProps>(({ item, handleClick, darkTheme }) => {
  const isMobile = useIsMobile();
  const [blockValues, setBlockValues] = useState({});

  const setValue = useCallback(
    (type: TYPES) => (value: string) => {
      setBlockValues((prev) => ({
        ...prev,
        [type]: value,
      }));
    },
    [],
  );

  const { key: type, pageUrl, picture, pictureForDarkTheme, title, label, text, buttonText, buttonTextMobile } = item;

  const handleUserEvent = useCallback(async () => {
    const value = blockValues[type];
    let url = `${pageUrl}${type === TYPES.CREDIT ? value || 500000 : value}`;

    if (type === TYPES.OSAGO) {
      try {
        const { token } = await getRegNumberToken(value);

        // Redirect to vehicle
        url = `/osago/?regNumberToken=${token}`;
      } catch {
        // Redirect to index
        url = `/osago/?platenumber=${value}`;
      }
    }

    handleClick(url);
  }, [blockValues, handleClick, pageUrl, type]);

  const handleAdvClick = useCallback(
    (e) => {
      e.stopPropagation();
      const inputElement = e.target.closest('[data-js=input]');
      if (inputElement) return;

      handleUserEvent();
    },
    [handleUserEvent],
  );

  const handleEnterEvent = useCallback(
    (event) => {
      if (event.keyCode === 13) {
        handleUserEvent();
      }
    },
    [handleUserEvent],
  );

  const pictureBlock = useMemo(() => {
    const inner = darkTheme && pictureForDarkTheme ? pictureForDarkTheme : picture;

    return (
      inner && (
        <div className={styles.pic}>
          <picture>{inner}</picture>
        </div>
      )
    );
  }, [darkTheme, picture, pictureForDarkTheme]);

  return (
    <div className={classNames(styles.block, styles[`block--big`], styles[`block--${type}`])} onClick={handleAdvClick}>
      {pictureBlock}
      <Space direction={'vertical'} justify={'space-between'} className={styles.texts}>
        <div>
          <div className={styles.header}>
            <Typography.Heading level={4} className={styles.title} as={'h2'}>
              {title}
            </Typography.Heading>
            {label}
          </div>
          <Typography.Text
            size={isMobile ? 12 : 14}
            className={cn(isMobile ? 'h-mt-12 h-color-D60' : 'h-mt-8 h-color-D60', styles.limitedWidth)}
          >
            {text}
          </Typography.Text>
        </div>
        <div className={cn(styles.form, 'h-mt-20', styles.limitedWidth)}>
          <div data-js={'input'} className={styles.inputWrapper}>
            {type === TYPES.OSAGO && <OsagoForm setValue={setValue(type)} handleEnterEvent={handleEnterEvent} />}
            {type === TYPES.CREDIT && (
              <CreditForm setValue={setValue(type as TYPES)} handleEnterEvent={handleEnterEvent} />
            )}
          </div>
          {!!buttonText && (
            <div className={cn(styles.button)}>
              <Button color={'gray'} variant={'outlined'} size={isMobile ? 60 : 44} block={isMobile}>
                {isMobile && buttonTextMobile ? buttonTextMobile : buttonText}
              </Button>
            </div>
          )}
          {type === TYPES.OSAGO && (
            <Typography.Text size={14} className={styles.link}>
              <Typography.Link strong>Нет номера</Typography.Link>
            </Typography.Text>
          )}
        </div>
      </Space>
    </div>
  );
});
